import React, { useContext, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { Skeleton, Empty } from "antd";
import useAxiosInstance from "../../lib/useAxiosInstance";
import YojnaTrackerContext from "../../context/yojnaTrackerContext";
import YojnaTrackerForm from "./YojnaTrackerForm";
import YojnaTrackerDetailModal from "./YojnaTrackerDetailModal";
import YojnaTrackerBulkUpload from "./YojnaTrackerBulkUpload";
import "../../styles/YojnaTracker.css";

const DEFAULT_COLOR = "#6B7280";

const YojnaTrackerBoard = () => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const yojnaContext = useContext(YojnaTrackerContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeCats, setActiveCats] = useState(["All"]);
  const [detailScheme, setDetailScheme] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  const fetchSchemes = async () => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    const response = await axiosInstance.get("api/yojna-tracker", { params });
    return response?.data;
  };

  const { data, isLoading } = useQuery(
    ["yojnaTrackerSchemes", searchTerm],
    fetchSchemes,
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const schemes = useMemo(() => data?.data || [], [data]);
  const categories = data?.meta?.categories || [];

  const filtered = useMemo(() => {
    if (activeCats.includes("All")) return schemes;
    return schemes.filter((s) => activeCats.includes(s.category));
  }, [schemes, activeCats]);

  const dated = useMemo(
    () => schemes.filter((s) => s.launchDate).map((s) => ({ ...s, launchDate: new Date(s.launchDate) })),
    [schemes]
  );

  const timeline = useMemo(() => {
    if (!dated.length) return null;
    let years = dated.map((s) => s.launchDate.getFullYear());
    let minY = Math.min(...years);
    let maxY = Math.max(...years);
    if (minY === maxY) {
      minY -= 2;
      maxY += 2;
    } else {
      minY -= 1;
      maxY += 1;
    }
    const span = maxY - minY;
    const width = Math.max(700, span * 90);
    const yearTicks = [];
    for (let y = minY; y <= maxY; y++) {
      yearTicks.push({ year: y, pct: ((y - minY) / span) * 100 });
    }
    const nodes = dated.map((s) => {
      const yearFrac = s.launchDate.getFullYear() + s.launchDate.getMonth() / 12;
      return { ...s, pct: ((yearFrac - minY) / span) * 100 };
    });
    return { yearTicks, nodes, width };
  }, [dated]);

  const headerStats = useMemo(() => {
    const catSet = new Set(schemes.map((s) => s.category));
    let range = "—";
    if (dated.length) {
      const years = dated.map((s) => s.launchDate.getFullYear());
      const min = Math.min(...years);
      const max = Math.max(...years);
      range = min === max ? String(min) : `${min} – ${max}`;
    }
    return { total: schemes.length, catCount: catSet.size, range };
  }, [schemes, dated]);

  const colorFor = (name) => {
    const hit = categories.find((c) => c.name === name);
    return hit?.color || DEFAULT_COLOR;
  };

  const toggleCat = (cat) => {
    if (cat === "All") {
      setActiveCats(["All"]);
      return;
    }
    setActiveCats((prev) => {
      let next = prev.includes("All") ? [] : [...prev];
      if (next.includes(cat)) next = next.filter((c) => c !== cat);
      else next.push(cat);
      return next.length ? next : ["All"];
    });
  };

  const openAdd = () => {
    yojnaContext.updateEditData({});
    yojnaContext.updateAddMode(true);
    yojnaContext.updateOpenModal(true);
  };

  const openEdit = (scheme) => {
    yojnaContext.updateEditData(scheme);
    yojnaContext.updateAddMode(false);
    yojnaContext.updateOpenModal(true);
    setDetailScheme(null);
  };

  const handleDelete = async (scheme) => {
    if (!window.confirm(`Delete "${scheme.scheme}"? This cannot be undone.`)) return;
    try {
      const response = await axiosInstance.delete(`api/yojna-tracker/${scheme._id}`);
      toast.success(response?.data?.message || "Scheme deleted");
      queryClient.invalidateQueries("yojnaTrackerSchemes");
      setDetailScheme(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <div className="yt-page">
      <header className="yt-header">
        <p className="yt-eyebrow">Scheme Launch &amp; Impact Registry</p>
        <div className="yt-header-row">
          <div>
            <h1>Yojna Tracker</h1>
            <p className="yt-sub">
              A launch-to-impact record of public schemes — overview, verified achievements and source links,
              filterable by category.
            </p>
          </div>
          <div className="yt-header-actions">
            <button className="yt-btn yt-btn-ghost" onClick={() => setIsBulkUploadOpen(true)}>
              ⬆ Bulk Upload
            </button>
            <button className="yt-btn yt-btn-primary" onClick={openAdd}>
              + Add Scheme
            </button>
          </div>
        </div>
        <div className="yt-headerstats">
          <div className="yt-hstat">
            <div className="yt-num">{headerStats.total}</div>
            <div className="yt-lbl">Schemes tracked</div>
          </div>
          <div className="yt-hstat">
            <div className="yt-num">{headerStats.catCount}</div>
            <div className="yt-lbl">Categories</div>
          </div>
          <div className="yt-hstat">
            <div className="yt-num">{headerStats.range}</div>
            <div className="yt-lbl">Launch span</div>
          </div>
        </div>
      </header>

      <div className="yt-toolbar">
        <div className="yt-toolbar-inner">
          <div className="yt-search-wrap">
            <input
              type="text"
              placeholder="Search schemes by name or overview..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="yt-chips">
            <div
              className="yt-chip"
              data-active={activeCats.includes("All")}
              onClick={() => toggleCat("All")}
            >
              All
            </div>
            {categories.map((c) => (
              <div
                key={c.name}
                className="yt-chip"
                data-active={activeCats.includes(c.name)}
                onClick={() => toggleCat(c.name)}
              >
                <span className="yt-dot" style={{ background: c.color }} />
                {c.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="yt-timeline-section">
        <div className="yt-timeline-title">Launch Timeline</div>
        <div className="yt-timeline-scroll">
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : timeline ? (
            <div className="yt-timeline" style={{ minWidth: timeline.width }}>
              <div className="yt-axis" />
              {timeline.yearTicks.map((t) => (
                <React.Fragment key={t.year}>
                  <div className="yt-year-tick" style={{ left: `${t.pct}%` }} />
                  <div className="yt-year-label" style={{ left: `${t.pct}%` }}>
                    {t.year}
                  </div>
                </React.Fragment>
              ))}
              {timeline.nodes.map((n) => (
                <div
                  key={n._id}
                  className="yt-node"
                  style={{ left: `${n.pct}%` }}
                  onClick={() => setDetailScheme(n)}
                >
                  <div className="yt-tip">
                    {n.scheme} · {n.launchDate.getFullYear()}
                  </div>
                  <div className="yt-pt" style={{ background: colorFor(n.category) }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="yt-timeline-empty">No parsable launch dates to plot.</div>
          )}
        </div>
      </section>

      <main className="yt-main">
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : filtered.length ? (
          <div className="yt-grid">
            {filtered.map((s) => (
              <div className="yt-card" key={s._id} onClick={() => setDetailScheme(s)}>
                <div className="yt-card-top">
                  <span className="yt-cat-tag" style={{ background: colorFor(s.category) }}>
                    {s.category}
                  </span>
                  <button
                    className="yt-card-edit-btn"
                    title="Edit this scheme"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(s);
                    }}
                  >
                    ✎
                  </button>
                </div>
                <h3>{s.scheme}</h3>
                <div className="yt-date">📅 {s.launchDateRaw || "Date not recorded"}</div>
                <p className="yt-excerpt">{s.overview || "No overview provided."}</p>
                <div className="yt-viewmore">View full details →</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="yt-empty-state">
            <Empty
              description={
                schemes.length === 0
                  ? 'No schemes yet — click "+ Add Scheme" above to create the first entry.'
                  : "No schemes match your search or category filter."
              }
            />
          </div>
        )}
      </main>

      {detailScheme && (
        <YojnaTrackerDetailModal
          scheme={detailScheme}
          colorFor={colorFor}
          onClose={() => setDetailScheme(null)}
          onEdit={() => openEdit(detailScheme)}
          onDelete={() => handleDelete(detailScheme)}
        />
      )}

      {isBulkUploadOpen && <YojnaTrackerBulkUpload onClose={() => setIsBulkUploadOpen(false)} />}

      {yojnaContext.isOpenModal && <YojnaTrackerForm />}
    </div>
  );
};

export default YojnaTrackerBoard;
